import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import multiplayerService from '../services/multiplayerService';
import { initializeEcho } from '../services/websocketService';
import WaitingRoom from '../components/multiplayer/WaitingRoom';
import MultiplayerGameContainer from '../components/multiplayer/MultiplayerGameContainer';
import './MultiplayerRoomPage.css';

const MultiplayerRoomPage = () => {
  const { roomCode } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [room, setRoom] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Initialize WebSocket connection
  useEffect(() => {
    initializeEcho();
  }, []);

  // Fetch room data on mount
  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const response = await multiplayerService.getRoom(roomCode);
        if (response.success) {
          let roomData = response.data;

          // Auto-join: if the room is still in the lobby and the current
          // user isn't a participant yet (e.g. they arrived via a QR link),
          // join them before rendering. Existing participants and in-progress
          // games skip this entirely.
          const alreadyJoined = roomData.participants?.some(p => p.id === user?.id);
          if (roomData.status === 'waiting' && !alreadyJoined) {
            try {
              const joinResponse = await multiplayerService.joinRoom(roomCode);
              if (joinResponse.success) {
                roomData = joinResponse.data;
              } else {
                setError(joinResponse.message || 'Could not join this room');
                return;
              }
            } catch (joinErr) {
              setError(joinErr.response?.data?.message || 'Could not join this room');
              return;
            }
          }

          setRoom(roomData);

          // If game is already in progress, fetch current round
          if (response.data.status === 'playing' && response.data.current_round > 0) {
            try {
              const roundResponse = await multiplayerService.getRound(roomCode, response.data.current_round);
              if (roundResponse.success) {
                setGameData({
                  room: response.data,
                  round: roundResponse.data,
                });
              }
            } catch (err) {
              console.error('Failed to fetch round:', err);
            }
          }
        } else {
          setError(response.message || 'Room not found');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load room');
      } finally {
        setLoading(false);
      }
    };

    fetchRoom();
  }, [roomCode, user?.id]);

  // Handle game start
  const handleGameStart = (data) => {
    setGameData(data);
    setRoom(prev => ({
      ...prev,
      status: 'playing',
      current_round: 1,
    }));
  };

  // Handle leave room
  const handleLeaveRoom = () => {
    navigate('/multiplayer');
  };

  // Handle game end
  const handleGameEnd = () => {
    navigate('/multiplayer');
  };

  if (loading) {
    return (
      <div className="room-page-loading">
        <div className="spinner"></div>
        <p>Loading room...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="room-page-error">
        <div className="error-content">
          <h2>Oops!</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/multiplayer')}>
            Back to Multiplayer
          </button>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="room-page-error">
        <div className="error-content">
          <h2>Room Not Found</h2>
          <p>The room you're looking for doesn't exist or has expired.</p>
          <button onClick={() => navigate('/multiplayer')}>
            Back to Multiplayer
          </button>
        </div>
      </div>
    );
  }

  // Show game if in progress
  if ((room.status === 'playing' || gameData) && gameData?.round) {
    return (
      <MultiplayerGameContainer
        room={room}
        initialRound={gameData.round}
        onGameEnd={handleGameEnd}
      />
    );
  }

  // Show waiting room
  if (room.status === 'waiting') {
    return (
      <div className="multiplayer-room-page">
        <div className="container">
          <WaitingRoom
            room={room}
            onGameStart={handleGameStart}
            onLeave={handleLeaveRoom}
          />
        </div>
      </div>
    );
  }

  // Room completed or abandoned
  if (room.status === 'completed' || room.status === 'abandoned') {
    return (
      <div className="room-page-error">
        <div className="error-content">
          <h2>Game Ended</h2>
          <p>This game has already ended.</p>
          <button onClick={() => navigate('/multiplayer')}>
            Back to Multiplayer
          </button>
        </div>
      </div>
    );
  }

  // Game in progress but round data couldn't be loaded (or unknown status)
  return (
    <div className="room-page-error">
      <div className="error-content">
        <h2>Game In Progress</h2>
        <p>This game has already started and your session could not be resumed.</p>
        <button onClick={() => navigate('/multiplayer')}>
          Back to Multiplayer
        </button>
      </div>
    </div>
  );
};

export default MultiplayerRoomPage;
